// Copilote sourcé.
//
// La garantie anti-hallucination est ARCHITECTURALE, pas déclarative : le
// serveur relit les citations dans le texte de la réponse, rejette celles qui
// désignent un objet inconnu, et n'appelle même pas le modèle si aucune source
// pertinente n'existe. L'interface doit rendre cette rigueur VISIBLE — d'où
// l'indice de confiance, le mode de réponse et l'avertissement affichés tels
// quels, sans enjolivement.
import { useState, type FormEvent } from 'react';
import { useCopilot } from '../api/queries';
import type { CopilotSource } from '../api/types';
import { Button, Eyebrow, Note, Spinner } from '../components/ui';

function SourceCard({ source }: { source: CopilotSource }) {
  // Dédoublonné : le locuteur et le persona sont parfois la même personne, et
  // « Karim · Karim » a l'air d'un bug.
  const meta = [...new Set([
    source.persona ?? source.speaker,
    source.study,
    source.document,
    source.timecode,
    source.sourceRef,
  ].filter(Boolean))];

  return (
    <li className="source">
      <div className="source-head">
        <span className="source-id t-mono">{source.id}</span>
        <span className="source-relevance t-mono">{source.relevance}%</span>
      </div>
      <p className="source-excerpt">« {source.excerpt} »</p>
      {meta.length > 0 ? <p className="source-meta">{meta.join(' · ')}</p> : null}
    </li>
  );
}

export function CopilotScreen({ projectId }: { projectId: string }) {
  const [question, setQuestion] = useState('');
  const copilot = useCopilot(projectId);
  const answer = copilot.data;

  function submit(event: FormEvent) {
    event.preventDefault();
    const q = question.trim();
    if (!q || copilot.isPending) return;
    copilot.mutate(q);
  }

  const confidence = answer?.confidence;
  const level = confidence?.level ?? 'none';

  return (
    <main className="screen">
      <div className="zy-page-head__texte">
        <Eyebrow>Copilote</Eyebrow>
        <h1 className="t-h1">Interroger le référentiel</h1>
        <p className="zy-intro">
          Chaque réponse cite ses sources et affiche son indice de confiance. Sans source
          pertinente, le copilote refuse de répondre plutôt que d’inventer.
        </p>
      </div>

      <form className="copilot-form" onSubmit={submit}>
        <label className="sr-only" htmlFor="copilot-q">
          Votre question
        </label>
        <input
          id="copilot-q"
          className="zy-input copilot-input"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="Quelles frictions connues sur la saisie terrain ?"
          autoComplete="off"
        />
        <Button type="submit" variant="accent" loading={copilot.isPending} disabled={!question.trim()}>
          Demander
        </Button>
      </form>

      {copilot.isPending ? (
        <div className="screen-loading">
          <Spinner label="Recherche dans le référentiel…" />
        </div>
      ) : null}

      {copilot.isError ? (
        <Note tone="erreur" role="alert">
          {copilot.error instanceof Error ? copilot.error.message : 'Le copilote est injoignable.'}
        </Note>
      ) : null}

      {answer ? (
        <section className="answer">
          {/* Le mode dit d'où vient la réponse. « extractive » = pas de clé LLM
              configurée : les preuves brutes, honnêtement étiquetées. */}
          {answer.mode === 'extractive' ? (
            <p className="mode-note">
              Mode extractif : aucun modèle de langage n’est configuré sur le serveur. Les preuves
              sont affichées telles quelles, sans synthèse.
            </p>
          ) : null}
          {answer.warning ? <Note tone="avertissement">{answer.warning}</Note> : null}

          <p className="answer-text">{answer.answer}</p>

          {confidence && confidence.value > 0 ? (
            <div className="confidence">
              <div className="confidence-head">
                <Eyebrow>Indice de confiance</Eyebrow>
                <span className={`confidence-value level-${level}`}>{confidence.value}%</span>
              </div>
              <progress className="zy-progress" value={confidence.value} max={100} aria-label="Indice de confiance" />
              <p className="confidence-label">{confidence.label}</p>
            </div>
          ) : null}

          {answer.sources.length > 0 ? (
            <>
              <h2 className="t-mono sources-title">
                {answer.sources.length} source{answer.sources.length > 1 ? 's' : ''} citée
                {answer.sources.length > 1 ? 's' : ''}
              </h2>
              <ul className="sources">
                {answer.sources.map((s) => (
                  <SourceCard key={s.id} source={s} />
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}
